/**
 * floorOrder — the order "Browse the Floor" shows real listings in.
 *
 * Collectibles first, highest price first; then every other listing in the
 * order it arrived (the server's: featured first, then newest). Throughout,
 * one seller never shows twice in a row unless no order of what is left can
 * avoid it, and that is the only thing that moves a listing out of those
 * orders.
 *
 * WHY. Blake, 2026-09-27, backing Tyler's ask on card 1218886636169221 for the
 * Bleecker card show: "all 72 prioritized in the feed ... in order by highest
 * to lowest value, but no single users items should be shown twice in a row
 * unless absolutely necessary." Printed QR codes at the show send people to
 * the website, and the newest-first floor opened on fashion. The rest is
 * spaced too (Blake, the same day), after the live floor showed one seller 12
 * in a row among the other 105 listings.
 *
 * WHERE IT RUNS. app.js's browseItems applies it through
 * window.__gbzOrderFloor (installed by installFloorListings), AFTER its
 * category and deal-type filters, so a "For sale" tab is spaced on what it
 * shows rather than inheriting gaps its filter removed. The category page's
 * full grid goes through browseItems, and the subcategory pages apply the same
 * hook (Blake, 2026-09-27). LISTINGS itself stays
 * in the server's order: fixed-position rails ("Picked for You" reads
 * LISTINGS[0..19]), search, "More From", Top Collections and the profile grid
 * all read that array, and reordering it put one seller in five of the six
 * "Picked for You" slots (adversarial review, 2026-09-27).
 *
 * PRICE is DemoListing.declaredValue, which mapListingRow fills with the
 * DISPLAYED price (cashPrice ?? declaredValue): the number the card prints, so
 * the order agrees with what a visitor reads. aiVerifiedValue is deliberately
 * not used: it is an estimate, not the price the card prints. A collection
 * piece (collectionOnly: not for sale) is never pulled into the collectibles
 * run; it keeps its place with the rest.
 *
 * ONLY WHAT THE FLOOR LOADED IS PROMOTED. Rows also reach LISTINGS after boot,
 * appended: a saved listing from GET /api/favorites (at every signed-in boot,
 * any status), a listing the route fallback opened (a sold item tapped
 * anywhere), a search hit, a seller's listings from profileFixups' bulk merge,
 * and __gbzApplyEdit's row when an edit puts it back on sale. Some of these
 * are sold or pending, and nothing on a row says so (DemoListing has no
 * status), so promoting them by price put a sold $3,000 card first on the
 * floor with "Make offer" (adversarial review, 2026-09-27). installFloorListings
 * passes `promotable`, which lets through only the ids it installed; merged
 * rows keep their place at the end, as they always have. The cost: a row
 * merged after boot that IS for sale waits for the next page load to be
 * promoted.
 */

export const PRIORITY_CATEGORY = "collectibles";

interface Orderable {
  category: string;
  declaredValue: number;
  seller: string;
  collectionOnly?: boolean;
}

/** Number(): a string price sorts by its value, not as zero. Anything not a finite number sorts last. */
const priceOf = (l: Orderable): number => {
  const n = Number(l.declaredValue);
  return Number.isFinite(n) ? n : 0;
};

/**
 * The fewest same-seller neighbors any order of the m listings still to place
 * can have, right after one of `last`'s went down: `cLast` is how many of
 * `last`'s remain, `maxOther` the most any OTHER seller has left.
 *
 * A seller with c of the m remaining needs c - 1 others between its own, or c
 * when it is `last` (its first one cannot go next); each separator the other
 * m - c listings cannot supply is one repeat nothing can avoid. That shortfall
 * grows with c, and only one seller can run short (it would hold more than
 * half of what is left), so two numbers decide it: 2c - m - 1 for the largest
 * other seller, 2c - m for `last`. 0 means an order with no repeat exists.
 * floorAllListings.test.ts checks the result against every permutation of
 * small floors.
 */
function fewestRepeats(maxOther: number, cLast: number, m: number): number {
  return Math.max(0, 2 * maxOther - m - 1, 2 * cLast - m);
}

/**
 * Seller spacing over a list already in priority order (highest price first).
 * At each step it takes the earliest listing whose placement still allows the
 * fewest repeats any order could have, and among those it avoids repeating
 * the last seller whenever it can, so a repeat nothing can prevent comes as
 * late as possible rather than at the top. A pure greedy ("anyone but the
 * last seller") can paint itself into a corner and repeat a seller at the end
 * when a no-repeat order existed. Among equals, the earlier (higher-priced)
 * listing always goes first. `lastBefore` is the seller placed just ahead of
 * this list, so the seam counts too.
 */
export function spaceBySeller<T extends { seller: string }>(ordered: readonly T[], lastBefore: string | null = null): T[] {
  const pool = [...ordered];
  const counts = new Map<string, number>(); // only sellers with listings left
  for (const l of pool) counts.set(l.seller, (counts.get(l.seller) ?? 0) + 1);
  const out: T[] = [];
  let last = lastBefore;
  while (pool.length > 0) {
    // The two largest counts left: once a seller places one, the most any
    // OTHER seller has left is the top count, or the runner-up if it holds it.
    let top: string | null = null;
    let first = 0;
    let second = 0;
    for (const [seller, c] of counts) {
      if (c > first) {
        second = first;
        first = c;
        top = seller;
      } else if (c > second) second = c;
    }
    const m = pool.length - 1;
    let pick = -1;
    let bestTotal = Number.POSITIVE_INFINITY;
    let bestNow = Number.POSITIVE_INFINITY;
    const tried = new Set<string>();
    for (let i = 0; i < pool.length && tried.size < counts.size; i++) {
      const seller = pool[i].seller;
      if (tried.has(seller)) continue; // a seller's earliest listing stands for all of its listings
      tried.add(seller);
      const now = seller === last ? 1 : 0;
      const total = now + fewestRepeats(seller === top ? second : first, (counts.get(seller) ?? 0) - 1, m);
      if (total < bestTotal || (total === bestTotal && now < bestNow)) {
        bestTotal = total;
        bestNow = now;
        pick = i;
      }
    }
    const [next] = pool.splice(pick, 1);
    const left = (counts.get(next.seller) ?? 0) - 1;
    if (left > 0) counts.set(next.seller, left);
    else counts.delete(next.seller);
    out.push(next);
    last = next.seller;
  }
  return out;
}

/**
 * The floor order. Returns a new array and never mutates its input; every
 * listing that goes in comes out exactly once. A collectible `promotable`
 * refuses keeps its place with the rest.
 */
export function orderFloor<T extends Orderable>(listings: readonly T[], promotable: (l: T) => boolean = () => true): T[] {
  const priority: { l: T; i: number }[] = [];
  const rest: T[] = [];
  listings.forEach((l, i) => {
    if (l.category === PRIORITY_CATEGORY && !l.collectionOnly && promotable(l)) priority.push({ l, i });
    else rest.push(l);
  });
  // Highest price first; equal prices keep the order they arrived in.
  priority.sort((a, b) => priceOf(b.l) - priceOf(a.l) || a.i - b.i);
  const lead = spaceBySeller(priority.map((p) => p.l));
  // The rest keep their arrival order as their priority and are spaced the
  // same way, from the seller the collectibles ended on, so the seam counts.
  return [...lead, ...spaceBySeller(rest, lead.length > 0 ? lead[lead.length - 1].seller : null)];
}
